import {
  CalendarEvent,
  Charge,
  Move,
  Mover,
  MoverInventoryItem,
  Notification,
} from "@/types";
import { baseApi } from "../baseApi";

export type Overview = {
  jobs: number;
  revenue: number;
  payments: number;
  leads: {
    data: Move[];
    total: number;
  };
  estimated: {
    data: Move[];
    total: number;
  };
  lost: {
    data: Move[];
    total: number;
  };
  booked: {
    data: Move[];
    total: number;
  };
  scheduled: {
    data: Move[];
    total: number;
  };
  sources: {
    name: string;
    leads: number;
    booked: number;
    revenue: number;
  }[];
};

type Metrics = {
  moves: number;
  revenue: number;
  leads: {
    total: number;
    confirmed?: number;
    estimated?: number;
    new?: number;
    lost?: number;
  };
  bookings: number;
  sources?: {
    name: string;
    count: number;
    revenue: number;
  }[];
};

type BusinessSummary = {
  current_month: Metrics;
  previous_month: Metrics;
};

const moverApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMe: builder.query({
      query: () => `users/me`,
    }),

    getUserMover: builder.query({
      query: () => `users/mymover`,
    }),

    getMover: builder.query<Mover, { id: number }>({
      query: ({ id }) => `mover?id=${id}`,
    }),

    getInventoryItems: builder.query<MoverInventoryItem[], void>({
      query: () => `mover/inventory-items`,
    }),

    updateMover: builder.mutation({
      query: ({ data }) => ({
        url: `mover`,
        method: "PUT",
        body: data,
      }),
    }),

    addUser: builder.mutation({
      query: ({ data }) => ({
        url: `users`,
        method: "POST",
        body: data,
      }),
    }),

    deactivateUser: builder.mutation({
      query: ({ user_id }) => ({
        url: `users/${user_id}/deactivate`,
        method: "POST",
      }),
    }),

    sendUserInvite: builder.mutation({
      query: ({ user_id }) => ({
        url: `users/${user_id}/send-invite`,
        method: "POST",
      }),
    }),

    sendCrewInvite: builder.mutation({
      query: ({ data }) => ({
        url: `users/invite_crew`,
        method: "POST",
        body: data,
      }),
    }),

    businessSummary: builder.query<
      BusinessSummary,
      { after?: string; before?: string }
    >({
      query: (params) => ({
        url: `metrics/business_summary`,
        params,
      }),
    }),

    overview: builder.query<Overview, { after?: string; before?: string }>({
      query: (params) => ({
        url: `dashboard/overview`,
        params,
      }),
    }),

    getNotifications: builder.query<
      {
        data: Notification[];
        pagination: {
          current_page: number;
          total_pages: number;
          total_count: number;
        };
      },
      { page: number; per_page?: number }
    >({
      query: (params) => ({
        url: `notifications`,
        params,
      }),
    }),

    markAllNotificationsAsRead: builder.mutation({
      query: () => ({
        url: `notifications/mark_all_as_read`,
        method: "POST",
      }),
    }),

    getDocumentSignedUrl: builder.mutation<
      { data: { signed_url: string; key: string } },
      { filename: string; content_type: string }
    >({
      query: ({ filename, content_type }) => ({
        url: `mover/signed_url`,
        method: "POST",
        body: { filename, content_type },
      }),
    }),

    getPayments: builder.query<Charge[], { before?: string; after?: string }>({
      query: ({ before, after }) =>
        `mover/payments?before=${before}&after=${after}`,
    }),

    getCalendarEvents: builder.query<
      CalendarEvent[],
      { nature?: string; after?: string; before?: string }
    >({
      query: ({ nature, after, before }) => ({
        url: "calendar_events",
        params: { nature, after, before },
      }),
    }),
  }),
});

export const {
  useGetMeQuery,
  useGetUserMoverQuery,
  useGetMoverQuery,
  useBusinessSummaryQuery,
  useUpdateMoverMutation,
  useGetDocumentSignedUrlMutation,
  useAddUserMutation,
  useSendCrewInviteMutation,
  useDeactivateUserMutation,
  useSendUserInviteMutation,
  useGetInventoryItemsQuery,
  useGetPaymentsQuery,
  useGetCalendarEventsQuery,
  useOverviewQuery,
  useGetNotificationsQuery,
  useMarkAllNotificationsAsReadMutation,
} = moverApi;

export { moverApi };
